/*
 * Purpose: Simplified CreatePassword component for password input and validation only.
 * Boundary: Composition only; wires hook state into layout; no OTP, no mode switching.
 * Invariants:
 * - Exposes isValid and password via context for parent access.
 * - Delegates all state and validation logic to useCreatePassword.
 * - ASCII only; no Unicode in comments or strings.
 */

// Imports
import { createContext, useContext, useEffect } from "react";
import CreatePasswordLayout from "./createpassword/createpassword-layout";
import { useCreatePassword } from "./createpassword/createpassword-hooks";

// Context for sharing password state upward
export const PasswordContext = createContext();

// Component CreatePassword:
// Headless wrapper syncing state with parent via context
export default function CreatePassword({
    auth = {},
    hideSecureMessage = false,
    showChips,
    showHeading
}) {
    // State Management - initialize hook with provided services
    const props = useCreatePassword({
        auth: auth,
        hideSecureMessage,
        showChips,
        showHeading
    });

    // Context - get setter from parent
    const { setPasswordState } = useContext(PasswordContext) || {};

    // Effect: Update parent via context
    useEffect(() => {
        if (setPasswordState) {
            setPasswordState({ isValid: props.isValid, password: props.password });
        }
    }, [props.isValid, props.password, setPasswordState]);

    // Rendering - delegate structure and UI to layout
    return <CreatePasswordLayout {...props} />;
}

// Metadata - friendly name for React DevTools
CreatePassword.displayName = "CreatePassword";
